/**
 * Wizard Store - 2025 Pinia Best Practices
 *
 * Manages the complete learning test flow including:
 * - Basic child information
 * - Multi-phase question answering (85 questions)
 * - Score calculation (9 learning dimensions)
 * - Result generation and analysis
 * - Payment processing integration
 * - API synchronization with retry logic
 * - Test session tracking
 *
 * Architecture:
 * - Composition API for better TypeScript
 * - Pinia persist for progress preservation
 * - Retry logic for critical operations
 * - Non-blocking background sync
 * - Comprehensive error handling
 *
 * @see {@link https://pinia.vuejs.org/core-concepts/} Pinia Documentation
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { sections, questions, wizardPhases, TOTAL_QUESTIONS } from '../data/learningTest';
import { getApiClient } from '../composables/useApiClient';
import { TestSessionType, SECTION_REGISTRY, rawToPercentage } from '@monorepo/types';
import { retryWithBackoff, getUserFriendlyErrorMessage } from '../utils/retry';

import type { BasicInfo, TestScores, OnboardingContext } from '@monorepo/types';

// ===========================
// TYPES
// ===========================

/**
 * Frontend-specific analysis result type (for UI display, not API contract)
 */
interface LocalAnalysisResult {
  testId: string
  parentSummary: string
  teacherSummary: string
  detailedAnalysis: string
  recommendations?: string[]
  sectionScores?: TestScores
  insights?: {
    strengths: string[]
    challenges: string[]
    learningStyle: string
    recommendations: string[]
  }
}

/**
 * Wizard phase identifier
 * - 'basic-info': Initial child information
 * - 1: Phase 1 - Learning style questions (visual, auditory, reading, kinesthetic, integrated)
 * - 2: Phase 2 - Special considerations (giftedness, processing, sensitivity, ADHD)
 */
type WizardPhase = 1 | 2 | 'basic-info'

// ===========================
// STORE DEFINITION
// ===========================

export const useWizardStore = defineStore('wizard', () => {
  // ===========================
  // STATE
  // ===========================

  /**
   * Basic child information (from onboarding)
   * ✅ EXTENDED: Now includes childInterests and childGender
   */
  const basicInfo = ref<BasicInfo>({
    childName: '',
    childAge: 0,
    childSchool: '',
    childSituation: '',
    childInterests: '', // ← NEW
    childGender: 'other' // ← NEW
  })

  /**
   * Onboarding context (challenges, goals, parent feeling)
   * ✅ NEW: Stores onboarding-specific data separately
   */
  const onboardingContext = ref<OnboardingContext>({
    challenges: undefined,
    parentFeelings: undefined,
    selectedGoals: undefined,
    startedAt: undefined,
    completedAt: undefined
  })

  /**
   * User answers to test questions (questionId -> answerValue 1-5)
   */
  const answers = ref<Record<number, number>>({})

  /**
   * Calculated scores across 9 learning dimensions
   */
  const scores = ref<TestScores>({
    visual: 0,
    auditory: 0,
    reading: 0,
    kinesthetic: 0,
    integrated: 0,
    giftedness: 0,
    auditoryProcessing: 0,
    highSensitivity: 0,
    adhd: 0
  })

  // Navigation state
  /**
   * Current step within the active phase (1-indexed)
   */
  const step = ref<number>(1)

  /**
   * Current wizard phase
   */
  const currentPhase = ref<WizardPhase>('basic-info')

  /**
   * Unique test identifier for result tracking
   */
  const testId = ref<string | null>(null)

  /**
   * Generated analysis result (premium or basic)
   */
  const analysisResult = ref<LocalAnalysisResult | null>(null)

  /**
   * Phase completion tracking
   */
  const phase1Complete = ref<boolean>(false)
  const phase2Complete = ref<boolean>(false)

  /**
   * User-facing error message
   */
  const errorMessage = ref<string | null>(null)

  // Test timing state
  /**
   * Test start timestamp (milliseconds since epoch)
   */
  const testStartTime = ref<number | null>(null)

  /**
   * Test end timestamp (milliseconds since epoch)
   */
  const testEndTime = ref<number | null>(null)

  // ===========================
  // COMPUTED (GETTERS)
  // ===========================

  /**
   * Basic info getter (for compatibility)
   */
  const getBasicInfo = computed<BasicInfo>(() => basicInfo.value)
  const getAnswers = computed<Record<number, number>>(() => answers.value)
  const getScores = computed<TestScores>(() => scores.value)

  // Wizard navigation getters - properly typed
  const currentSectionId = computed<string | null>(() => {
    if (currentPhase.value === 'basic-info') {
      return 'basic-info'
    }
    const phaseSections = sections.filter(s => s.phase === currentPhase.value)
    return phaseSections[step.value - 1]?.id || null
  })

  const currentPhaseData = computed(() => {
    if (currentPhase.value === 'basic-info') {
      return { id: 'basic-info', title: 'Basic Information', description: 'Enter basic information about your child' }
    }
    return wizardPhases.find(p => p.id === currentPhase.value)
  })

  const totalStepsInCurrentPhase = computed(() => {
    if (currentPhase.value === 'basic-info') {
      return 1
    }
    return sections.filter(s => s.phase === currentPhase.value).length
  })

  const isPhase1Complete = computed(() => {
    const phase1Sections = sections.filter(s => s.phase === 1)
    return phase1Sections.every(section => isSectionComplete(section.id))
  })

  const isPhase2Complete = computed(() => {
    const phase2Sections = sections.filter(s => s.phase === 2)
    return phase2Sections.every(section => isSectionComplete(section.id))
  })

  // Computed properties for UI
  const isBasicInfoComplete = computed(() => {
    const info = basicInfo.value
    const age = info.childAge ? Number(info.childAge) : 0
    return !!(info.childName && age > 0 && info.childSchool && info.childSituation)
  })

  const sectionComplete = computed(() => {
    if (currentPhase.value === 'basic-info') {
      return isBasicInfoComplete.value
    }
    return isSectionComplete(currentSectionId.value || '')
  })

  // Navigation computed properties
  const isFirstStep = computed(() => {
    return currentPhase.value === 'basic-info' && step.value === 1
  })

  const isLastStep = computed(() => {
    if (currentPhase.value === 'basic-info') {
      return false // Basic info is never the last step
    }
    return step.value === totalStepsInCurrentPhase.value && currentPhase.value === 2
  })

  const canGoNext = computed(() => {
    if (currentPhase.value === 'basic-info') {
      return isBasicInfoComplete.value
    }
    return sectionComplete.value
  })

  const canGoBack = computed(() => {
    return !isFirstStep.value
  })

  // Test timing computed properties
  const testCompletionTime = computed(() => {
    if (testStartTime.value && testEndTime.value) {
      return Math.round((testEndTime.value - testStartTime.value) / 1000) // in seconds
    }
    return 0
  })

  const testCompletionTimeMinutes = computed(() => {
    return Math.round(testCompletionTime.value / 60)
  })

  // ===========================
  // ACTIONS
  // ===========================

  /**
   * Update basic child information
   * ✅ UPDATED: Now handles all 6 fields including childInterests and childGender
   */
  function setBasicInfo(info: Partial<BasicInfo>): void {
    basicInfo.value = { ...basicInfo.value, ...info }
  }

  /**
   * Update onboarding context (challenges, goals, parent feeling)
   * ✅ NEW: Stores onboarding-specific data separately
   */
  function setOnboardingContext(context: Partial<OnboardingContext>): void {
    onboardingContext.value = { ...onboardingContext.value, ...context }
  }

  /**
   * Update answer for a specific question and calculate scores
   * Also submits to backend for progress tracking (non-blocking)
   *
   * @throws {Error} If question number is invalid
   */
  function updateAnswer(questionNumber: number, value: number): void {
    // Validate question number to prevent data corruption
    if (!Number.isInteger(questionNumber) || questionNumber < 1 || questionNumber > 90) {
      console.error(`❌ Invalid question number: ${questionNumber}. Must be between 1 and 90.`)
      throw new Error(`Invalid question number: ${questionNumber}`)
    }

    // Validate answer value (1-5 for Likert scale)
    if (!Number.isInteger(value) || value < 1 || value > 5) {
      console.error(`❌ Invalid answer value: ${value}. Must be between 1 and 5.`)
      throw new Error(`Invalid answer value: ${value}`)
    }

    answers.value[questionNumber] = value
    calculateScores() // Auto-calculate scores when answer is updated

    // Submit answer to backend in background (non-blocking)
    submitAnswerToBackend(questionNumber, value).catch(error => {
      console.warn(`Failed to submit answer Q${questionNumber} to backend:`, error)
    })
  }

  /**
   * Alias for updateAnswer (compatibility with LikertRow component)
   */
  function setAnswer(questionNumber: number, value: number): void {
    updateAnswer(questionNumber, value)
  }

  /**
   * Set current step number
   */
  function setStep(stepNumber: number): void {
    step.value = stepNumber
  }

  /**
   * Set current phase and reset to first step
   */
  function setPhase(phaseNumber: WizardPhase): void {
    currentPhase.value = phaseNumber
    step.value = 1 // Reset to first step of new phase
  }

  /**
   * Mark phase 1 as complete
   */
  function completePhase1(): void {
    phase1Complete.value = true
  }

  /**
   * Mark phase 2 as complete
   */
  function completePhase2(): void {
    phase2Complete.value = true
  }

  // Navigation methods
  function goNext(): void {
    if (!canGoNext.value) {
      return
    }

    if (currentPhase.value === 'basic-info') {
      // Move from basic info to phase 1
      setPhase(1)
      return
    }

    if (step.value < totalStepsInCurrentPhase.value) {
      // Move to next step in current phase
      step.value++
    } else if (currentPhase.value === 1) {
      // Move from phase 1 to phase 2
      completePhase1()
      setPhase(2)
    } else if (currentPhase.value === 2) {
      // Phase 2 complete - this should be handled by the component
      completePhase2()
      setPhase(2)
    }
  }

  function goBack(): void {
    if (!canGoBack.value) {
      return
    }

    if (currentPhase.value === 1 && step.value === 1) {
      // Go back to basic info
      setPhase('basic-info')
      return
    }

    if (currentPhase.value === 2 && step.value === 1) {
      // Go back to last step of phase 1
      setPhase(1)
      const phase1Steps = sections.filter(s => s.phase === 1).length
      step.value = phase1Steps
      return
    }

    if (step.value > 1) {
      // Go back one step in current phase
      step.value--
    }
  }

  // Test timing methods
  function startTestTiming(): void {
    testStartTime.value = Date.now()
  }

  function endTestTiming(): void {
    testEndTime.value = Date.now()
  }

  // Alias for resetStore - used by ResultsView
  function reset(): void {
    resetStore()
  }

  /**
   * Calculate percentage-based scores from raw answers
   *
   * Uses SECTION_REGISTRY for dynamic question ranges (no more hardcoded values).
   * Scores are calculated as: (rawScore / maxRawScore) * 100
   * where maxRawScore = questionCount × 5
   */
  function calculateScores(): void {
    // Initialize all scores to 0
    const newScores: TestScores = {
      visual: 0,
      auditory: 0,
      reading: 0,
      kinesthetic: 0,
      integrated: 0,
      giftedness: 0,
      auditoryProcessing: 0,
      highSensitivity: 0,
      adhd: 0
    };

    // Calculate raw scores for each section using SECTION_REGISTRY
    for (const section of SECTION_REGISTRY) {
      const [start, end] = section.questionRange;
      let rawScore = 0;

      // Sum answers in this section's range
      for (let q = start; q <= end; q++) {
        rawScore += (answers.value[q] || 0);
      }

      // Calculate max raw score: questionCount × 5
      const questionCount = end - start + 1;
      const maxRawScore = questionCount * 5;

      // Convert to percentage
      const percentage = rawToPercentage(rawScore, maxRawScore);

      // Assign to the corresponding score key
      const sectionId = section.id as keyof TestScores;
      if (sectionId in newScores) {
        newScores[sectionId] = percentage;
      }
    }

    scores.value = newScores;
  }

  /**
   * Get dominant learning styles based on current scores
   */
  function getDominantLearningStyles(): string[] {
    const styleScores = [
      { style: 'Visuell', score: scores.value.visual },
      { style: 'Auditiv', score: scores.value.auditory },
      { style: 'Lese-/Schreibtyp', score: scores.value.reading },
      { style: 'Kinästhetisch', score: scores.value.kinesthetic },
      { style: 'Integriert', score: scores.value.integrated },
    ]

    // Sort by score and return top 2-3 styles
    return styleScores
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
      .slice(0, 3)
      .filter((item) => (item.score ?? 0) > 20) // Only include significant scores
      .map((item) => item.style)
  }

  /**
   * Generate basic tips based on dominant learning styles
   */
  function generateBasicTips(dominantStyles: string[]): string[] {
    const tips: Record<string, string[]> = {
      'Visuell': [
        'Verwenden Sie bunte Marker und Textmarker beim Lernen',
        'Erstellen Sie gemeinsam Mindmaps und Lernposter',
        'Nutzen Sie Bilder und Diagramme zur Veranschaulichung'
      ],
      'Auditiv': [
        'Lesen Sie Lerninhalte laut vor',
        'Nutzen Sie Lernlieder und Reime',
        'Führen Sie Diskussionen über den Lernstoff'
      ],
      'Lese-/Schreibtyp': [
        'Ermutigen Sie zum Mitschreiben während Erklärungen',
        'Lassen Sie Zusammenfassungen schreiben',
        'Nutzen Sie Karteikarten mit Texten'
      ],
      'Kinästhetisch': [
        'Erlauben Sie Bewegung beim Lernen',
        'Nutzen Sie praktische Experimente',
        'Verwenden Sie haptische Lernmaterialien'
      ],
      'Integriert': [
        'Kombinieren Sie verschiedene Lernmethoden',
        'Wechseln Sie zwischen visuellen, auditiven und praktischen Ansätzen',
        'Nutzen Sie multimediale Lernmaterialien'
      ]
    }

    const allTips: string[] = []
    dominantStyles.forEach(style => {
      if (tips[style]) {
        allTips.push(...tips[style])
      }
    })

    return allTips.slice(0, 5) // Return top 5 tips
  }

  /**
   * Generate learning style summary for parent and teacher
   */
  function generateLearningStyleSummary(dominantStyles: string[]): { parentSummary: string, teacherSummary: string } {
    const childName = basicInfo.value.childName || 'Ihr Kind'
    const primaryStyle = dominantStyles[0] || 'Ausgewogen'

    const parentSummary = `${childName} zeigt eine starke Präferenz für ${primaryStyle} Lernen. ${
      dominantStyles.length > 1
        ? `Zusätzlich sind auch ${dominantStyles.slice(1).join(' und ')} Lernstile ausgeprägt.`
        : ''
    } Diese Grundauswertung gibt Ihnen erste wichtige Einblicke in die natürlichen Lernpräferenzen Ihres Kindes.`

    const teacherSummary = `Für Lehrkräfte: ${childName} profitiert besonders von ${primaryStyle.toLowerCase()}en Lernmethoden. ${
      dominantStyles.length > 1
        ? `Eine Kombination mit ${dominantStyles.slice(1).join(' und ').toLowerCase()}en Ansätzen kann zusätzlich hilfreich sein.`
        : ''
    } Berücksichtigen Sie diese Präferenzen bei der Unterrichtsgestaltung.`

    return { parentSummary, teacherSummary }
  }

  function updateScores(newScores: Partial<TestScores>): void {
    scores.value = { ...scores.value, ...newScores }
  }

  /**
   * Generate a unique test ID if one doesn't exist
   * @returns The test ID (existing or newly generated)
   */
  function generateTestId(): string {
    if (!testId.value) {
      testId.value = 'test_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
      console.log('Generated testId:', testId.value)
    }
    return testId.value
  }

  // Wizard navigation methods
  function isSectionComplete(sectionId: string): boolean {
    const section = sections.find(s => s.id === sectionId)
    if (!section || !section.questionRange) return false

    // Get questions for this section using the question range
    const [startRange, endRange] = section.questionRange
    const sectionQuestions = questions.filter(q => q.id >= startRange && q.id <= endRange)

    // Check if all questions in this section have been answered
    return sectionQuestions.every(q => answers.value[q.id] !== undefined && answers.value[q.id] > 0)
  }

  // Loading state
  const isLoading = ref<boolean>(false)

  // Retry state for critical operations
  const retryCount = ref<number>(0)
  const lastRetryError = ref<string | null>(null)
  const isRetrying = ref<boolean>(false)
  const testSubmissionPending = ref<boolean>(false) // Indicates test needs to be submitted

  /**
   * Generate basic result using client-side calculation only
   * No backend API call needed for basic analysis
   */
  async function generateBasicResult(): Promise<void> {
    console.log('Generating basic result (client-side)...')

    // Reset error state and set loading
    errorMessage.value = null
    isLoading.value = true

    // Generate test ID if not exists
    if (!testId.value) {
      testId.value = 'test_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
    }

    try {
      // Calculate scores first
      calculateScores()

      // Generate basic analysis result using client-side logic
      const dominantStyles = getDominantLearningStyles()
      const basicTips = generateBasicTips(dominantStyles)
      const learningStyleSummary = generateLearningStyleSummary(dominantStyles)

      // Create local analysis result for UI display
      analysisResult.value = {
        testId: testId.value,
        parentSummary: learningStyleSummary.parentSummary,
        teacherSummary: learningStyleSummary.teacherSummary,
        detailedAnalysis: `Basierend auf den Antworten zeigt ${basicInfo.value.childName || 'Ihr Kind'} eine Präferenz für ${dominantStyles.join(', ')} Lernstile. Diese Auswertung gibt Ihnen detaillierte Einblicke in die Lernpräferenzen.`,
        recommendations: basicTips,
        sectionScores: { ...scores.value },
        insights: {
          strengths: [`Starke ${dominantStyles[0]}-Präferenz`, 'Ausgeglichene Lernansätze'],
          challenges: ['Weitere Analyse empfohlen für detaillierte Einblicke'],
          learningStyle: dominantStyles[0] || 'Ausgewogen',
          recommendations: basicTips
        }
      }

      console.log('✅ Basic result generated successfully (client-side)')

      // Submit completed test to backend for tracking (non-blocking)
      await submitCompletedTest()

    } catch (error) {
      console.error('Error generating basic result:', error)

      // Set user-friendly error message
      errorMessage.value = 'Es ist ein Fehler bei der Auswertung aufgetreten. Wir zeigen Ihnen eine Beispiel-Auswertung.'

      // Create fallback result
      await createFallbackResult()

    } finally {
      isLoading.value = false
    }
  }

  /**
   * Submit completed test to backend (for basic results)
   * Creates TestResult entity with status "completed" for tracking
   * Handles cases where test session was not started (e.g., due to bot detection)
   *
   * ✅ CRITICAL OPERATION: Uses retry logic with Pinia persistence
   * - 5 retry attempts over 30 seconds
   * - State persisted by Pinia (survives page refresh)
   * - Can be retried manually or automatically on app reopen
   */
  async function submitCompletedTest(): Promise<void> {
    console.log('Submitting completed test to backend with retry logic...')

    // Mark as pending submission (persisted by Pinia)
    testSubmissionPending.value = true
    isRetrying.value = true

    try {
      // End test timing when test is completed
      endTestTiming()

      // Calculate scores first
      calculateScores()

      // Prepare test completion data (only premium tier available)
      // Include basicInfo, answers, and onboardingContext to ensure complete TestResult creation
      // Answers are critical for session recovery scenarios (session deletion/expiration)
      const completionData = {
        scores: scores.value,
        analysisType: 'premium' as const,
        // Include answers for recovery scenarios - essential for data preservation
        answers: answers.value,
        // Include basicInfo for recovery scenarios (session deletion/expiration)
        basicInfo: basicInfo.value.childName ? {
          childName: basicInfo.value.childName,
          childAge: Number(basicInfo.value.childAge),
          childSchool: basicInfo.value.childSchool || '',
          childSituation: basicInfo.value.childSituation || '',
          childInterests: basicInfo.value.childInterests,
          childGender: basicInfo.value.childGender
        } : undefined,
        // Include onboarding context for complete test result
        onboardingContext: onboardingContext.value.challenges || onboardingContext.value.parentFeelings || onboardingContext.value.selectedGoals ? {
          challenges: onboardingContext.value.challenges,
          parentFeelings: onboardingContext.value.parentFeelings,
          selectedGoals: onboardingContext.value.selectedGoals,
          startedAt: onboardingContext.value.startedAt,
          completedAt: onboardingContext.value.completedAt
        } : undefined
      }

      console.log('Submitting test completion with retry:', completionData)

      // Use centralized test session service
      const apiClient = getApiClient()

      // Execute with retry logic
      const response = await retryWithBackoff(
        async () => {
          try {
            // Submit to test session completion endpoint with completion data
            // Backend will create TestResult and return testId
            const result = await apiClient.testSession.complete(completionData)
            console.log('✅ Test submitted successfully:', result)
            return result
          } catch (sessionError: unknown) {
            const err = sessionError as { status?: number; response?: { statusCode?: number } };
            // If test session was not found (e.g., due to bot detection during start),
            // try to start and complete in one go
            if (err?.status === 404 || err?.response?.statusCode === 404) {
              console.log('🔄 Test session not found, attempting to start and complete...')

              // Start test session first
              await apiClient.testSession.start({
                testType: TestSessionType.LEARNING_PROFILE,
                basicInfo: basicInfo.value.childName ? {
                  childName: basicInfo.value.childName,
                  childAge: Number(basicInfo.value.childAge),
                  childSchool: basicInfo.value.childSchool || '',
                  childSituation: basicInfo.value.childSituation || ''
                } : undefined,
                metadata: {
                  source: 'wizard_completion',
                  fallback: true
                }
              })

              // Then complete it with basicInfo included
              const retryResponse = await apiClient.testSession.complete(completionData)
              console.log('✅ Test session started and completed successfully:', retryResponse)
              return retryResponse
            } else {
              // Re-throw other errors for retry logic to handle
              throw sessionError
            }
          }
        },
        {
          maxAttempts: 5,
          initialDelay: 2000,
          maxDelay: 30000,
          backoffMultiplier: 2,
          onRetry: (attempt, error) => {
            retryCount.value = attempt
            lastRetryError.value = getUserFriendlyErrorMessage(error)
            console.warn(`🔄 Retry attempt ${attempt}/5:`, error)
          },
        }
      )

      // Success! Clear retry state
      testSubmissionPending.value = false
      isRetrying.value = false
      retryCount.value = 0
      lastRetryError.value = null

      // Store backend-generated testId for payment processing
      if (response?.data?.testId) {
        testId.value = response.data.testId
        console.log('✅ Backend-generated testId:', testId.value)
      }

    } catch (error: unknown) {
      // All retries failed - state is persisted by Pinia for later retry
      isRetrying.value = false
      lastRetryError.value = getUserFriendlyErrorMessage(error)

      console.error('❌ Test submission failed after all retries:', error)
      console.warn(`⚠️ ${lastRetryError.value}`)
      console.log('✅ Test data saved in Pinia store and can be retried when connection is restored')

      // Don't throw error - basic results should still work
      // testSubmissionPending remains true for manual/automatic retry
    }
  }

  /**
   * Process payment completion - Creates user, links session, submits test
   * This is the main function that should be called after successful payment
   * Uses strongly typed API service with discriminated unions - NO type assertions
   */
  async function processPaymentCompletion(paymentIntentId: string, customerEmail: string): Promise<void> {
    console.log('Processing payment completion...')

    // Reset error state and set loading
    errorMessage.value = null
    isLoading.value = true

    try {
      // Calculate scores first
      calculateScores()

      // Validate and prepare test data for submission with proper typing
      // Payment API requires all fields to be present and childAge to be a number
      if (!basicInfo.value.childName || !basicInfo.value.childAge || !basicInfo.value.childSchool || !basicInfo.value.childSituation) {
        throw new Error('Alle Pflichtfelder müssen ausgefüllt sein')
      }

      const testData = {
        testId: testId.value!,
        basicInfo: {
          childName: basicInfo.value.childName,
          childAge: Number(basicInfo.value.childAge),
          childSchool: basicInfo.value.childSchool,
          childSituation: basicInfo.value.childSituation
        },
        scores: scores.value,
        answers: answers.value
      }

      console.log('Processing payment with test data:', { paymentIntentId, customerEmail, testData })

      // Import the strongly typed payment API service
      const { processPaymentCompletion: processPayment } = await import('../services/typed-payment-api')

      // Call the strongly typed payment processing service
      // This uses discriminated unions and type guards - no type assertions needed
      const result = await processPayment({
        paymentIntentId,
        customerEmail,
        testData
      })

      // TypeScript guarantees result has the correct structure
      console.log('✅ Payment processed successfully:', result)
      console.log(`User created: ${result.userId}, Payment: ${result.paymentId}, Test: ${result.testResultId}`)

      // Complete the test session to create TestResult entity for admin dashboard
      try {
        console.log('🔍 [PaymentCompletion] Completing test session...')

        // Use centralized test session service
        const apiClient = getApiClient()

        const completionData = {
          scores: scores.value,
          analysisType: 'premium' as const,
          // Include answers for recovery scenarios - essential for data preservation
          answers: answers.value
        }

        try {
          // Complete the test session with premium analysis type
          await apiClient.testSession.complete(completionData)
          console.log('✅ Test session completed successfully')
        } catch (sessionError: unknown) {
          const err = sessionError as { status?: number; response?: { statusCode?: number } };
          // If test session was not found (e.g., due to bot detection during start),
          // try to start and complete in one go
          if (err?.status === 404 || err?.response?.statusCode === 404) {
            console.log('🔄 [PaymentCompletion] Test session not found, attempting to start and complete...')

            try {
              // Start test session first
              await apiClient.testSession.start({
                testType: TestSessionType.LEARNING_PROFILE,
                metadata: {
                  source: 'payment_completion',
                  fallback: true
                }
              })

              // Then complete it with answers included
              await apiClient.testSession.complete(completionData)
              console.log('✅ Test session started and completed successfully after payment')
            } catch (retryError) {
              console.warn('⚠️ Failed to start and complete test session on retry after payment:', retryError)
              // Still don't throw - payment was successful, session completion is secondary
            }
          } else {
            // Re-throw other errors for logging
            throw sessionError
          }
        }
      } catch (sessionError) {
        console.error('⚠️ Failed to complete test session (payment still successful):', sessionError)
        // Don't throw error - payment was successful, session completion is secondary
      }

    } catch (error) {
      console.error('Error processing payment:', error)
      errorMessage.value = 'Fehler bei der Zahlungsverarbeitung. Bitte kontaktieren Sie den Support.'
      throw error

    } finally {
      isLoading.value = false
    }
  }



  async function createFallbackResult(): Promise<void> {
    console.log('Creating fallback result...')

    // Create sectionScores mapping that matches what ResultsView expects
    const sectionScores: TestScores = scores.value

    // Set fallback result with more realistic content
    analysisResult.value = {
      testId: testId.value || '',
      parentSummary: `Basierend auf den Antworten zeigt ${basicInfo.value.childName || 'Ihr Kind'} verschiedene Lernpräferenzen. Diese Beispiel-Auswertung wurde erstellt, da die Serververbindung nicht verfügbar ist. Für eine vollständige Analyse kontaktieren Sie uns bitte.`,
      teacherSummary: `${basicInfo.value.childName || 'Das Kind'} zeigt in der Bewertung verschiedene Lernstile. Diese Beispiel-Empfehlungen wurden offline erstellt. Für detaillierte pädagogische Empfehlungen ist eine Online-Analyse erforderlich.`,
      detailedAnalysis: 'Fallback-Analyse erstellt aufgrund fehlender Serververbindung.',
      recommendations: ['Empfehlung wird nach Serververbindung generiert'],
      sectionScores: { ...sectionScores },
      insights: {
        strengths: ['Wird nach Analyse verfügbar'],
        challenges: ['Wird nach Analyse verfügbar'],
        learningStyle: 'Wird analysiert',
        recommendations: ['Wird nach Analyse verfügbar']
      }
    }
  }

  // Navigation methods used by WizardContainer
  function setError(message: string): void {
    errorMessage.value = message
  }

  function clearError(): void {
    errorMessage.value = null
  }

  function resetStore(): void {
    basicInfo.value = {
      childName: '',
      childAge: 0,
      childSchool: '',
      childSituation: '',
      childInterests: '',
      childGender: 'other'
    }
    answers.value = {}
    scores.value = {
      visual: 0,
      auditory: 0,
      reading: 0,
      kinesthetic: 0,
      integrated: 0,
      giftedness: 0,
      auditoryProcessing: 0,
      highSensitivity: 0,
      adhd: 0
    }
    onboardingContext.value = {
      challenges: undefined,
      parentFeelings: undefined,
      selectedGoals: undefined,
      startedAt: undefined,
      completedAt: undefined
    }
    step.value = 1
    currentPhase.value = 'basic-info'
    testId.value = null
    analysisResult.value = null
    phase1Complete.value = false
    phase2Complete.value = false
    isLoading.value = false
    errorMessage.value = null

    // Clear persisted data when explicitly resetting
    localStorage.removeItem('ulrich-wizard-store')
    console.log('✅ Wizard store reset and localStorage cleared')
  }

  // Clear localStorage on store initialization to ensure fresh start
  function clearOldPersistedData(): void {
    // Remove old persistence keys that might contain stale data
    const oldKeys = ['wizard-store', 'pinia-wizard']
    oldKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log(`🧹 Clearing old persisted data: ${key}`)
        localStorage.removeItem(key)
      }
    })
  }

  // Clear old data on initialization
  clearOldPersistedData()

  // Function to start a completely new test (clears everything)
  function startNewTest(): void {
    console.log('🆕 Starting new test - clearing all data...')
    resetStore()
    startTestTiming() // Start timing when test begins
  }

  /**
   * Submit answer to backend for incremental progress tracking
   * Non-blocking operation - doesn't affect UI if it fails
   */
  async function submitAnswerToBackend(questionNumber: number, value: number): Promise<void> {
    try {
      const apiClient = getApiClient()

      // Calculate current progress based on answered questions
      const totalAnswered = Object.keys(answers.value).length
      const estimatedProgress = Math.round((totalAnswered / TOTAL_QUESTIONS) * 100)

      await apiClient.testSession.answer({
        questionId: `q${questionNumber}`,
        answer: value,
        progress: estimatedProgress,
        currentStep: `question-${questionNumber}`,
        metadata: {
          answeredAt: new Date().toISOString(),
          totalAnswered
        }
      })

      console.log(`✅ Answer submitted to backend: Q${questionNumber} = ${value} (Progress: ${estimatedProgress}%)`)
    } catch (error) {
      // Log but don't throw - this is non-critical background tracking
      console.warn(`Failed to submit answer Q${questionNumber} to backend:`, error)
    }
  }

  /**
   * Retry pending test submission
   * Called manually by user or automatically on app reopen/network restore
   */
  async function retryPendingSubmission(): Promise<boolean> {
    if (!testSubmissionPending.value) {
      console.log('No pending test submission to retry')
      return false
    }

    console.log('🔄 Retrying pending test submission...')

    try {
      await submitCompletedTest()
      return true
    } catch (error) {
      console.error('Failed to retry test submission:', error)
      return false
    }
  }

  /**
   * Start test session in backend
   * Creates TestSession entity for tracking test progress
   * Sends basicInfo, testId, and onboarding data to backend for later TestResult creation
   */
  async function startTestSession(): Promise<void> {
    console.log('Starting test session in backend...')

    try {
      // Get session store to access tracking ID
      const { useSessionStore } = await import('./core/session')
      const sessionStore = useSessionStore()

      // Ensure session is initialized to get tracking ID
      if (!sessionStore.isInitialized) {
        await sessionStore.initializeSession()
      }

      // Generate testId if not exists
      if (!testId.value) {
        testId.value = 'test_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
        console.log('Generated testId:', testId.value)
      }

      // Get onboarding data from onboarding store
      const { useOnboardingStore } = await import('./onboarding')
      const onboardingStore = useOnboardingStore()
      const onboardingData = onboardingStore.formData

      // Use centralized test session service
      const apiClient = getApiClient()

      // Start test session with basicInfo, testId, and onboarding data for later TestResult creation
      const response = await apiClient.testSession.start({
        testType: TestSessionType.LEARNING_PROFILE,
        basicInfo: basicInfo.value.childName ? {
          childName: basicInfo.value.childName,
          childAge: Number(basicInfo.value.childAge),
          childSchool: basicInfo.value.childSchool || '',
          childSituation: basicInfo.value.childSituation || ''
        } : undefined,
        metadata: {
          source: 'frontend',
          startedAt: new Date().toISOString(),
          testId: testId.value,
          // Include onboarding data (challenges, goals, parent feeling)
          onboarding: {
            challenges: onboardingData.challenges || [],
            parentFeelings: onboardingData.parentFeelings || [],
            selectedGoals: onboardingData.selectedGoals || [],
            childInterests: onboardingData.childInterests || '',
            childGender: onboardingData.childGender || 'other'
          }
        }
      })

      console.log('✅ Test session started successfully:', response)

    } catch (error) {
      console.error('Error starting test session:', error)
      // Don't throw error - test should still work even if backend session creation fails
    }
  }

  return {
    // State
    basicInfo,
    onboardingContext, // ← NEW
    answers,
    scores,
    step,
    currentPhase,
    testId,
    analysisResult,
    phase1Complete,
    phase2Complete,
    isLoading,
    errorMessage,
    testStartTime,
    testEndTime,

    // Retry state
    retryCount,
    lastRetryError,
    isRetrying,
    testSubmissionPending,

    // Getters
    getBasicInfo,
    getAnswers,
    getScores,
    currentSectionId,
    currentPhaseData,
    totalStepsInCurrentPhase,
    isPhase1Complete,
    isPhase2Complete,

    // UI computed properties
    isBasicInfoComplete,
    sectionComplete,

    // Navigation computed properties
    isFirstStep,
    isLastStep,
    canGoNext,
    canGoBack,

    // Test timing computed properties
    testCompletionTime,
    testCompletionTimeMinutes,

    // Actions
    setBasicInfo,
    setOnboardingContext, // ← NEW
    updateAnswer,
    setAnswer,
    setStep,
    setPhase,
    completePhase1,
    completePhase2,

    // Navigation methods
    goNext,
    goBack,

    // Test timing methods
    startTestTiming,
    endTestTiming,
    updateScores,
    calculateScores,
    isSectionComplete,
    generateTestId,
    generateBasicResult,
    submitCompletedTest,
    retryPendingSubmission,
    processPaymentCompletion,
    reset,
    resetStore,
    startNewTest,
    startTestSession,
    createFallbackResult,

    // Error handling methods
    setError,
    clearError
  }
}, {
  // Enable selective persistence following Pinia best practices
  persist: {
    key: 'ulrich-wizard-store',
    storage: localStorage,
    // Only persist specific state that should survive page refresh
    pick: [
      'currentStep',
      'basicInfo',
      'onboardingContext', // ← NEW
      'answers',
      'scores',
      'testId',
      'sessionId',
      'currentPhase',
      'currentQuestionIndex',
      // Retry state - critical for data recovery
      'testSubmissionPending',
      'retryCount',
      'lastRetryError',
    ],
    // Serialize/deserialize functions for complex data
    serializer: {
      serialize: JSON.stringify,
      deserialize: JSON.parse,
    },
    // Debug persistence (remove afterRestore as it's not supported in this version)
    debug: true
  }
})
